import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useSubscription } from '../../hooks/useSubscription';
import './styles.css';
import logo from '../../images/inline_eternavaults-logo.png'
import NotificationBell from '../NotificationBell';
import { FaBars, FaTimes, FaChevronDown } from 'react-icons/fa';

export default function SimplifiedNavigation() {
  const { isAuthenticated, loginWithRedirect, logout, user, isLoading } = useAuth0();
  const { hasActiveSubscription } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const dropdownRef = React.useRef(null);
  const [isBurgerOpen, setIsBurgerOpen] = useState(false);
  const burgerMenuRef = React.useRef(null);

  const foundingCircle = location.pathname.includes('founding-circle');

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setIsMoreMenuOpen(false);
      }
    }
    if (isDropdownOpen || isMoreMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, isMoreMenuOpen]);

  // Close burger menu on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (burgerMenuRef.current && !burgerMenuRef.current.contains(event.target)) {
        setIsBurgerOpen(false);
      }
    }
    if (isBurgerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isBurgerOpen]);

  // Define user personas and their navigation
  const getNavigationForUser = () => {
    if (!isAuthenticated) {
      return {
        primary: [
          { name: 'About', link: '/about' },
          { name: 'How It Works', link: '/how' },
          { name: 'Security', link: '/security' },
          { name: 'Contact', link: '/contact' }
        ],
        secondary: [
          { name: 'Case Studies', link: '/case-studies' },
          { name: 'Family Legacy Guide', link: '/family-legacy-guide' }
        ]
      };
    }

    if (!hasActiveSubscription) {
      return {
        primary: [
          { name: 'Choose Plan', link: '/choose-plan' }
        ],
        secondary: [
          { name: 'Security', link: '/security' }
        ]
      };
    }

    // Authenticated users with subscription
    const primary = [
      { name: 'My Vault', link: '/member-selection' },
      { name: 'Account', link: '/family-continuity-structure' }
    ];

    // Add Messages only if plan has access (Apex+)
    // Note: This component doesn't have plan info, so we'll let the route handle it
    // But we can add it conditionally if plan info becomes available
    primary.splice(1, 0, { name: 'Messages', link: '/message-center' });

    return {
      primary,
      secondary: [
        { name: 'Security', link: '/security' },
        { name: 'Family Legacy Guide', link: '/family-legacy-guide' },
        { name: 'Case Studies', link: '/case-studies' },
        { name: 'About', link: '/about' },
        { name: 'Contact', link: '/contact' }
      ]
    };
  };

  const navigation = getNavigationForUser();

  // Desktop navigation
  if (!isMobile) {
    return (
      <nav className="simplified-nav-bar">
        <div className="nav-left">
          <Link to="/" className="nav-logo">
            <img className='logo-img' src={logo} alt="Eterna Vaults Logo" />
          </Link>
        </div>

        {!foundingCircle && (
          <div className="nav-links">
            {/* Primary Navigation - Always Visible */}
            {navigation.primary.map((item, index) => (
              <Link
                key={index}
                to={item.link}
                className='nav-link primary'
              >
                {item.name}
              </Link>
            ))}

            {/* More Menu for Secondary Items */}
            {navigation.secondary.length > 0 && (
              <div className="more-menu" ref={dropdownRef}>
                <button
                  className="nav-link more-button"
                  onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                >
                  More <FaChevronDown className={`chevron ${isMoreMenuOpen ? 'open' : ''}`} />
                </button>

                {isMoreMenuOpen && (
                  <div className="more-dropdown">
                    {navigation.secondary.map((item, index) => (
                      <Link
                        key={index}
                        to={item.link}
                        className='more-link'
                        onClick={() => setIsMoreMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="nav-auth">
          {!isAuthenticated && !foundingCircle && (
            <>
              <Link to="/how" className='nav-cta'>
                Start your vault
              </Link>
              <button
                className="nav-btn"
                onClick={() => window.location.href = '/signup'}
              >
                Request Private Access
              </button>
              <button
                className="nav-btn"
                onClick={() => loginWithRedirect()}
              >
                Log In
              </button>
            </>
          )}
          {isLoading ? null : isAuthenticated ? (
            <>
              <NotificationBell />
              <span className="nav-user">{user?.name || user?.email}</span>
              <button
                className="nav-btn"
                onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              >
                Log Out
              </button>
            </>
          ) : null}
        </div>
      </nav>
    );
  }

  // Mobile navigation
  if (!isAuthenticated) {
    return (
      <nav className="simplified-nav-bar mobile-nav">
        <div className="mobile-nav-row">
          <button
            className="burger-menu-btn"
            aria-label="Open menu"
            onClick={() => setIsBurgerOpen(true)}
          >
            <FaBars size={28} />
          </button>
          <Link to="/" className="nav-logo">
            <img className='logo-img' src={logo} alt="Eterna Vaults Logo" />
          </Link>
          <div className="nav-auth mobile-auth">
            <Link to="/how" className='nav-cta'>
              Start your vault
            </Link>
            <button
              className="nav-btn"
              onClick={() => window.location.href = '/signup'}
            >
              Request Private Access
            </button>
            <button
              className="nav-btn"
              onClick={() => loginWithRedirect()}
            >
              Log In
            </button>
          </div>
        </div>
        {isBurgerOpen && (
          <div className="burger-menu-overlay">
            <aside className="burger-menu" ref={burgerMenuRef}>
              <button
                className="burger-menu-close"
                aria-label="Close menu"
                onClick={() => setIsBurgerOpen(false)}
              >
                <FaTimes size={28} />
              </button>

              {/* Primary Navigation */}
              <div className="burger-section">
                <h3>Main</h3>
                {navigation.primary.map((item, index) => (
                  <Link
                    key={index}
                    to={item.link}
                    className="burger-link primary"
                    onClick={() => setIsBurgerOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>

              {/* Secondary Navigation */}
              {navigation.secondary.length > 0 && (
                <div className="burger-section">
                  <h3>More</h3>
                  {navigation.secondary.map((item, index) => (
                    <Link
                      key={index}
                      to={item.link}
                      className="burger-link secondary"
                      onClick={() => setIsBurgerOpen(false)}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </aside>
          </div>
        )}
      </nav>
    );
  }

  // Mobile navigation for authenticated users
  return (
    <>
      <nav className="simplified-nav-bar mobile-nav">
        <div className="mobile-nav-row">
          <button
            className="burger-menu-btn"
            aria-label="Open menu"
            onClick={() => setIsBurgerOpen(true)}
          >
            <FaBars size={28} />
          </button>
          <Link to="/" className="nav-logo">
            <img className='logo-img' src={logo} alt="Eterna Vaults Logo" />
          </Link>
          <NotificationBell />
          <span className="nav-user">{user?.name || user?.email}</span>
          <button
            className="nav-btn"
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          >
            Log Out
          </button>
        </div>
        {isBurgerOpen && (
          <div className="burger-menu-overlay">
            <aside className="burger-menu" ref={burgerMenuRef}>
              <button
                className="burger-menu-close"
                aria-label="Close menu"
                onClick={() => setIsBurgerOpen(false)}
              >
                <FaTimes size={28} />
              </button>

              {/* Primary Navigation */}
              <div className="burger-section">
                <h3>Main</h3>
                {navigation.primary.map((item, index) => (
                  <Link
                    key={index}
                    to={item.link}
                    className="burger-link primary"
                    onClick={() => setIsBurgerOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>

              {/* Secondary Navigation */}
              {navigation.secondary.length > 0 && (
                <div className="burger-section">
                  <h3>More</h3>
                  {navigation.secondary.map((item, index) => (
                    <Link
                      key={index}
                      to={item.link}
                      className="burger-link secondary"
                      onClick={() => setIsBurgerOpen(false)}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </aside>
          </div>
        )}
      </nav>
    </>
  );
}
